import React from 'react';
import { useSelector } from 'react-redux';

import {
	Box,
	FormHelperText,
	MenuItem,
	Select,
	Typography,
	SelectChangeEvent,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { ShopRole } from 'common/types';
import { getRoleOptions, getRoleText } from '../utils';
import { useTranslation } from 'services/localization/useTranslation';

import * as UserSelectors from 'selectors/UserSelectors';
import { getShopRoles } from 'common/modules/users/utils';

interface Props {
	value: ShopRole[];
	onChange: (value: ShopRole[]) => void;
	disabled?: boolean;
	helperText?: string;
	error?: string;
}

const RolesSelect = (props: Props) => {
	const { value, onChange, disabled, helperText, error } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const activeUserRoles = useSelector(UserSelectors.userRoles);
	const userShopRoles = getShopRoles(activeUserRoles);

	const handleChange = (e: SelectChangeEvent) => {
		onChange([e.target.value as ShopRole]);
	};

	const renderValue = (value: unknown) => {
		if (!value) {
			return <Typography color="secondary">{t('users.selectRole', 'Select role')}</Typography>;
		}
		return getRoleText(value as ShopRole);
	};

	const renderOptions = () => {
		const roleOptions = getRoleOptions(userShopRoles);
		return roleOptions.map((option, index) => (
			<MenuItem
				key={option.value}
				value={option.value}
				className={classes.menuItem}
				divider={index < roleOptions.length - 1}
			>
				<Box width="100%">
					<Typography variant="body1" className={classes.label} gutterBottom>
						{option.label}
					</Typography>
					<Typography variant="body2" color="secondary" gutterBottom noWrap={false}>
						{option.description}
					</Typography>
				</Box>
			</MenuItem>
		));
	};

	return (
		<>
			<Select
				fullWidth
				displayEmpty
				variant="outlined"
				className={classes.select}
				disabled={disabled}
				value={value[0] ?? ''}
				onChange={handleChange}
				renderValue={renderValue}
				classes={{ select: classes.selectRoot }}
				MenuProps={{
					classes: { paper: classes.menuPaper },
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'right',
					},
					transformOrigin: {
						vertical: 'top',
						horizontal: 'right',
					},
				}}
			>
				{renderOptions()}
			</Select>
			{(error || helperText) && (
				<Box mx={1.75}>
					<FormHelperText error={!!error}>{error || helperText}</FormHelperText>
				</Box>
			)}
		</>
	);
};

const useStyles = makeStyles((theme) =>
	createStyles({
		select: {
			height: 40,
		},
		selectRoot: {
			paddingBottom: theme.spacing(1.25),
			paddingTop: theme.spacing(1.25),
		},
		label: {
			fontWeight: 500,
		},
		menuPaper: {
			maxWidth: 536,
			width: `calc(100% - ${theme.spacing(4)})`,
		},
		menuItem: {
			whiteSpace: 'break-spaces',
		},
	}),
);

export default RolesSelect;
